import { useRef, useState } from 'react';
import cn from 'classnames';
import FlagRu from '@/assets/icons/flags/ru.svg?react';
import FlagEn from '@/assets/icons/flags/en.svg?react';
import { brand } from '@/brand';
import useClickOutside from '@/shared/hooks/useClickOutside';
import { PillDropdown } from '@/shared/ui/PillDropdown';
import { pillDropdownItemClassName } from '@/shared/ui/pillDropdownItemClassName';
import { LANGUAGES, setLanguage, type Lang } from '@/shared/i18n';
import { useLanguage } from '@/shared/i18n/useLanguage';
import { useTranslation } from 'react-i18next';

// Переключатель языка кабинета. Вид задаёт бренд (`brand.layout.languageSwitcher`):
// флаг текущего языка со списком флагов по клику (serious, wapclick) или
// пилюля с глобусом и выпадающей карточкой, как в макете telecom (LanguageSelect
// ниже).
//
// У флагов два варианта, потому что мест у переключателя два:
// - `compact` — флаг 26px без подписи. Шапка serious и правый верхний угол
//   мобильной шторки: там рядом стоит колокольчик, и иконка читается как
//   иконка. Размер взят у лендинга serious (26×26).
// - `row` — строка сайдбара: флаг 24px по сетке пунктов меню (иконка 24 +
//   gap 8) и название языка справа. Нужен там, где переключатель стоит сам по
//   себе — у бренда без колокольчика (`features.notifications: false`) одинокая
//   иконка читалась как случайный элемент.
//
// Форма флага вариантом не управляется: она одинакова во всех местах и приходит
// из `brand.layout.languageSwitcher` (serious — круг, wapclick — квадрат).
//
// Флаги — те же файлы, что у лендинга (landings/serious/img/flag-*.svg),
// скопированы в ассеты кабинета: лендинг отдаётся nginx'ом из своей папки, его
// пути кабинету недоступны. Сами SVG квадратные, круг даёт `rounded-full`.
//
// На гостевых экранах переключателя нет вовсе, язык там выбирается на лендинге.
//
// Выбор сохраняется в localStorage['sp_lang'] — том же ключе, что у лендинга
// (общий origin), поэтому язык переживает переход между ними.

const FLAGS: Record<Lang, typeof FlagRu> = { ru: FlagRu, en: FlagEn };

type Variant = 'compact' | 'row';

type LanguageSwitcherProps = {
  className?: string;
  // Сторона, к которой прижат выпадающий список. Раскрывается он вниз: и в
  // шапке, и в верхней части сайдбара под кнопкой есть место.
  align?: 'left' | 'right';
  variant?: Variant;
  // Свёрнутый сайдбар (64px): подпись остаётся в DOM и гаснет через opacity —
  // так же, как подписи пунктов меню и кнопки выхода. Только для `row`.
  collapsed?: boolean;
  // Только для пилюли ('select'): тон фона (см. PillDropdown) и раскрытие вверх у
  // нижнего края экрана (меню на весь экран у compactNav = 'rail-topbar').
  tone?: 'light' | 'inverse' | 'deep';
  placement?: 'bottom' | 'top';
  // Только для пилюли: самоназвание языка («· Русский») — лишь с этой ширины,
  // ниже один код. Нужно строке аккаунта на планшете: там она делит ряд с
  // заголовком страницы, и «Редактировать поток» без этого не влезал.
  nameFrom?: 'lg';
};

const Flag = ({ lang, variant }: { lang: Lang; variant: Variant }) => {
  const FlagIcon = FLAGS[lang];

  return (
    <FlagIcon
      className={cn(
        'shrink-0',
        variant === 'row' ? 'size-6' : 'size-[26px]',
        brand.layout.languageSwitcher === 'flag-square' ? 'rounded-[3px]' : 'rounded-full',
      )}
    />
  );
};

// Пилюля из макета telecom: глобус, код и самоназвание языка («EN · English»),
// по клику — карточка со списком. Вид общий с пилюлей баланса (PillDropdown):
// в строке аккаунта они стоят рядом и обязаны раскрываться одинаково. Сначала
// здесь был нативный <select> — его системный список рядом с карточкой
// кошельков выглядел чужим (замечание 23.09).
//
// Вариант места (`compact`/`row`) пилюле не нужен — она одинакова в шапке и в
// шторке, поэтому пропсы флагов здесь не читаются.
//
// Глобус инлайном: его нет ни в одном паке (в свой пак telecom его из макета
// не переносили — он нужен только здесь). Контур — Lucide (ISC), как в макете.
const languageLabel = (lang: Lang, name: string) => `${lang.toUpperCase()} · ${name}`;

const LanguageSelect = ({
  className,
  tone,
  placement,
  nameFrom,
}: Pick<LanguageSwitcherProps, 'className' | 'tone' | 'placement' | 'nameFrom'>) => {
  const { t } = useTranslation('common');
  const current = useLanguage();

  return (
    <PillDropdown
      className={className}
      tone={tone}
      placement={placement}
      popupRole="listbox"
      heading={t('language.switch')}
      ariaLabel={t('language.switch')}
      label={
        <>
          <svg
            aria-hidden
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth={2}
            strokeLinecap="round"
            strokeLinejoin="round"
            className="size-[15px] shrink-0 text-content-primary"
          >
            <circle cx="12" cy="12" r="10" />
            <path d="M12 2a14.5 14.5 0 0 0 0 20 14.5 14.5 0 0 0 0-20" />
            <path d="M2 12h20" />
          </svg>
          <b className="font-bold text-content-primary">
            {current.toUpperCase()}
            <span className={cn(nameFrom === 'lg' && 'hidden lg:inline')}>
              {' · '}
              {t(`language.${current}` as const)}
            </span>
          </b>
        </>
      }
    >
      {(close) =>
        LANGUAGES.map((lang) => (
          <button
            key={lang}
            type="button"
            role="option"
            aria-selected={lang === current}
            className={pillDropdownItemClassName(lang === current)}
            onClick={() => {
              close();
              void setLanguage(lang);
            }}
          >
            {languageLabel(lang, t(`language.${lang}` as const))}
          </button>
        ))
      }
    </PillDropdown>
  );
};

const LanguageFlags = ({
  className,
  align = 'right',
  variant = 'compact',
  collapsed = false,
}: LanguageSwitcherProps) => {
  const { t } = useTranslation('common');
  const current = useLanguage();
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  useClickOutside(ref, () => setOpen(false));
  const isRow = variant === 'row';

  const pick = (lang: Lang) => {
    setOpen(false);
    void setLanguage(lang);
  };

  return (
    <div ref={ref} className={cn('relative flex items-center', isRow && 'w-full', className)}>
      <button
        type="button"
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-label={t('language.switch')}
        title={isRow && !collapsed ? undefined : t(`language.${current}` as const)}
        onClick={() => setOpen((v) => !v)}
        className={cn(
          'flex cursor-pointer items-center',
          // Цвета и метрика строки — те же, что у пунктов меню и кнопки выхода
          // (см. getRootLinkClassName в MenuItem): на десктопе сайдбар
          // инверсный, на мобиле — светлый.
          isRow &&
            'w-full max-h-8 gap-2 overflow-hidden py-1 text-sm font-medium leading-5 text-content-primary hover:text-content-accent lg:text-content-inverse-muted lg:hover:text-content-inverse-accent',
        )}
      >
        <Flag lang={current} variant={variant} />
        {isRow && (
          <span
            className={cn(
              'whitespace-nowrap transition-opacity duration-200',
              collapsed ? 'opacity-0' : 'opacity-100',
            )}
          >
            {t(`language.${current}` as const)}
          </span>
        )}
      </button>

      {open && (
        <ul
          role="listbox"
          className={cn(
            'absolute top-full z-50 mt-2 flex min-w-[160px] flex-col gap-1 rounded-md border border-line-default bg-surface-card p-1 shadow-base',
            align === 'right' ? 'right-0' : 'left-0',
          )}
        >
          {LANGUAGES.map((lang) => (
            <li key={lang}>
              <button
                type="button"
                role="option"
                aria-selected={lang === current}
                onClick={() => pick(lang)}
                className={cn(
                  'flex w-full cursor-pointer items-center gap-2 whitespace-nowrap rounded-control-sm px-2 py-1 text-sm font-medium',
                  lang === current
                    ? 'bg-action-secondary-muted-selected text-content-primary'
                    : 'text-content-secondary hover:bg-surface-hover hover:text-content-primary',
                )}
              >
                <Flag lang={lang} variant={variant} />
                {t(`language.${lang}` as const)}
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

export const LanguageSwitcher = (props: LanguageSwitcherProps) =>
  brand.layout.languageSwitcher === 'select' ? (
    <LanguageSelect
      className={props.className}
      tone={props.tone}
      placement={props.placement}
      nameFrom={props.nameFrom}
    />
  ) : (
    <LanguageFlags {...props} />
  );
